/**
 * Category Showcase — Editorial Kinetic List.
 *
 * Statt eines weiteren Bento-Grids aus abgerundeten Glass-Boxen wird jede
 * Kategorie hier als großzügige redaktionelle Listenzeile inszeniert:
 *
 *   ── 01  ◉ icon   RESTAURANTS                           0% PROVISION ─
 *   ── 02  ◉ icon   SHOPS                                 0% PROVISION ─
 *   ── 03  ◉ icon   TAXIS                                 12€ FLATRATE ─
 *   …
 *
 * Visual / Motion:
 *   - Jede Zeile fliegt abwechselnd von rechts/links ein, kommt mit leichter
 *     Rotation und „magnetischem" Snap zur Ruhe.
 *   - Ein Spotlight wandert anschließend durch die Liste und hebt jeweils
 *     genau eine Kategorie heraus (Scale + Glow), wie ein Reveal-Reel.
 *   - Icon-Disc, Nummer, Hairline-Divider und Caption-Pill nehmen die
 *     rotierende Brand-Akzentfarbe an (Türkis → Violett → Pink).
 *
 * Items-Format (unverändert):
 *   "Titel | Caption | iconKey"
 *   z. B. "Restaurants | 0% Provision | shop"
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "./_mockHelpers.js";

const ICON_MAP: Record<string, string> = {
  restaurants: "shop",
  restaurant: "shop",
  food: "shop",
  produkte: "store",
  shop: "store",
  shops: "store",
  shopping: "store",
  taxi: "taxi",
  taxis: "taxi",
  transport: "truck",
  hotel: "hotel",
  hotels: "hotel",
  unterkünfte: "hotel",
  unterkunft: "hotel",
  unterkuenfte: "hotel",
  event: "event",
  events: "event",
  dienstleister: "service",
  dienstleistungen: "service",
  service: "service",
  services: "service",
  inserate: "message",
  inserat: "message",
  ads: "message",
  lieferung: "truck",
  delivery: "truck",
  shopping_mall: "store",
};

export const categoryGrid: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary; // #a855f7
  const pink = themeColors.accent;      // #ec4899
  const palette = [accent, violet, pink, accent, violet, pink, accent, violet, pink];

  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Restaurants | 0% Provision | shop",
        "Shops | 0% Provision | store",
        "Taxis | 12€ Flatrate | taxi",
        "Hotels | 12€ Flatrate | hotel",
        "Events | 12€ Flatrate | event",
        "Dienstleister | 12€ Flatrate | service",
        "Inserate | 12€ Flatrate | message",
      ]
  ).slice(0, 9);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const listId = `${clipPrefix}-list`;
  const rowIds = items.map((_, i) => `${clipPrefix}-row-${i}`);
  const numIds = items.map((_, i) => `${clipPrefix}-num-${i}`);
  const iconIds = items.map((_, i) => `${clipPrefix}-icon-${i}`);
  const titleIds = items.map((_, i) => `${clipPrefix}-title-${i}`);
  const capIds = items.map((_, i) => `${clipPrefix}-cap-${i}`);
  const dividerIds = items.map((_, i) => `${clipPrefix}-div-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const parsed = items.map((raw, i) => {
    const [title, caption, iconRaw] = splitItem(raw);
    const tt = (title ?? "").trim();
    const cap = (caption ?? "").trim();
    const iconKey = (iconRaw ?? "").trim() || (ICON_MAP[tt.toLowerCase()] ?? "shop");
    const color = palette[i % palette.length] ?? accent;
    return { title: tt, caption: cap, iconKey, color };
  });

  // Adaptive sizing: more items → tighter rows, smaller type.
  const n = parsed.length;
  const rowGap = isVertical ? Math.max(6, 18 - n) : Math.max(8, 22 - n);
  const titlePx = (isVertical ? md * 0.95 : lg * 0.85) * (n > 6 ? 0.88 : 1);
  const iconBox = isVertical ? Math.round(titlePx * 0.95) : Math.round(titlePx * 1.0);
  const iconPx = Math.round(iconBox * 0.55);
  const numPx = isVertical ? sm * 0.6 : sm * 0.65;
  const capPx = isVertical ? sm * 0.5 : sm * 0.55;
  const rowPadY = isVertical ? 12 : 14;
  const rowPadX = isVertical ? 14 : 22;

  function renderRow(idx: number, item: (typeof parsed)[number]): string {
    const id = rowIds[idx];
    const color = item.color;
    const numLabel = String(idx + 1).padStart(2, "0");
    return `<div id="${id}" style="position:relative;display:grid;grid-template-columns:auto auto 1fr auto;align-items:center;gap:${isVertical ? 16 : 24}px;padding:${rowPadY}px ${rowPadX}px;will-change:transform,opacity;">
      <!-- Number marker -->
      <div id="${numIds[idx]}" style="font-family:'Inter',ui-monospace,monospace;font-weight:${weights.semibold};font-size:${numPx}px;letter-spacing:2px;color:${withAlpha(themeColors.fg, 0.45)};font-feature-settings:'tnum';min-width:${Math.round(numPx * 2.2)}px;">${numLabel}</div>
      <!-- Icon disc -->
      <div id="${iconIds[idx]}" style="position:relative;width:${iconBox}px;height:${iconBox}px;border-radius:9999px;background:radial-gradient(circle at 30% 30%, ${withAlpha(color, 0.55)} 0%, ${withAlpha(color, 0.18)} 60%, ${withAlpha(themeColors.bg, 0.9)} 100%);display:flex;align-items:center;justify-content:center;color:${themeColors.fg};border:1px solid ${withAlpha(color, 0.55)};box-shadow:0 0 28px ${withAlpha(color, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.18)};">
        ${lucideIcon(item.iconKey, iconPx, themeColors.fg, 2.2)}
      </div>
      <!-- Title -->
      <div id="${titleIds[idx]}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titlePx}px;line-height:1;letter-spacing:-1.5px;color:${themeColors.fg};text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(item.title)}</div>
      <!-- Caption pill -->
      ${item.caption ? `<div id="${capIds[idx]}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${capPx}px;letter-spacing:1.4px;text-transform:uppercase;color:${color};padding:${isVertical ? "6px 10px" : "7px 14px"};border-radius:9999px;border:1px solid ${withAlpha(color, 0.55)};background:${withAlpha(color, 0.1)};white-space:nowrap;box-shadow:0 0 18px ${withAlpha(color, 0.35)};">${escapeHtml(item.caption)}</div>` : `<div id="${capIds[idx]}" style="opacity:0;"></div>`}
      ${idx < parsed.length - 1 ? `<!-- Hairline divider — only BETWEEN rows, never under the last one -->
      <div id="${dividerIds[idx]}" style="position:absolute;left:${rowPadX}px;right:${rowPadX}px;bottom:-${Math.round(rowGap / 2)}px;height:1px;background:linear-gradient(90deg, transparent 0%, ${withAlpha(themeColors.fg, 0.18)} 50%, transparent 100%);transform-origin:left center;"></div>` : `<div id="${dividerIds[idx]}" style="display:none;"></div>`}
    </div>`;
  }

  const rowsHtml = parsed.map((it, i) => renderRow(i, it)).join("");

  const headEyebrowPx = sm * 0.62;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 18 : 28}px;padding:${isVertical ? "64px 40px" : "72px 80px"};">
    ${headline ? `<div style="display:flex;flex-direction:column;align-items:center;gap:10px;">
      <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${headEyebrowPx}px;letter-spacing:5px;text-transform:uppercase;color:${withAlpha(accent, 0.85)};">Allyver · Marktplatz</div>
      <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.9}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1;text-align:center;text-wrap:balance;max-width:1100px;">${escapeHtml(headline)}</div>
      ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.7}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:880px;">${escapeHtml(sub)}</div>` : ""}
    </div>` : ""}
    <div id="${listId}" style="display:flex;flex-direction:column;gap:${rowGap}px;width:100%;max-width:${isVertical ? 960 : 1280}px;">
      ${rowsHtml}
    </div>
  </div>`;

  // ------------------------------------------------------------------
  // Animation timeline
  // ------------------------------------------------------------------
  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 14 }) : "",
    // Rows: alternating off-screen sides, slight rotation, scaled down.
    ...rowIds.map((id, i) => gsapSetLine(`#${id}`, {
      opacity: 0,
      x: i % 2 === 0 ? 180 : -180,
      rotation: i % 2 === 0 ? 4 : -4,
      scale: 0.92,
    })),
    // Icons start tiny + rotated so the "snap" reads.
    ...iconIds.map((id) => gsapSetLine(`#${id}`, { scale: 0, rotation: -90, transformOrigin: "50% 50%" })),
    // Dividers start collapsed (scaleX:0) so they sweep right after row lands.
    ...dividerIds.map((id) => gsapSetLine(`#${id}`, { scaleX: 0, transformOrigin: "left center" })),
    // Captions slip in slightly from the right.
    ...capIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, x: 12 })),
    // Numbers fade.
    ...numIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 6 })),
    // Titles slide up from below their final position.
    ...titleIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 18 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];

  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.0));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.18));

  // Stagger row entries. Scale stagger by item count + clip length so 7 items
  // still complete with enough air for the spotlight pass afterwards.
  const rowStart = 0.3;
  const totalEntryWindow = Math.min(1.4, Math.max(0.6, clipDurationSec * 0.45));
  const rowStep = totalEntryWindow / Math.max(parsed.length, 1);

  rowIds.forEach((id, i) => {
    const at = rowStart + i * rowStep;
    // Row flies in from the side, lands with magnetic ease.
    tweens.push(t.to(`#${id}`, {
      opacity: 1, x: 0, rotation: 0, scale: 1,
      duration: 0.55, ease: "raw:\"back.out(1.6)\"",
    }, at));
    // Number fades + lifts up.
    tweens.push(t.to(`#${numIds[i]}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, at + 0.08));
    // Icon snaps into place with a back-rotate.
    tweens.push(t.to(`#${iconIds[i]}`, { scale: 1, rotation: 0, duration: 0.55, ease: "raw:\"back.out(2.2)\"" }, at + 0.05));
    // Title rises into place.
    tweens.push(t.to(`#${titleIds[i]}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power3.out\"" }, at + 0.12));
    // Caption pill slides in from the right.
    tweens.push(t.to(`#${capIds[i]}`, { opacity: 1, x: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, at + 0.18));
    // Divider sweeps in left-to-right.
    tweens.push(t.to(`#${dividerIds[i]}`, { scaleX: 1, duration: 0.6, ease: "raw:\"power3.out\"" }, at + 0.1));
  });

  // ------------------------------------------------------------------
  // Spotlight pass — once all rows landed, sweep a "highlight" through
  // them so the eye reads each category in sequence. The highlight is
  // an icon scale-pulse + title color flash. Important: we leave a hard
  // safety margin (0.7s) before the outro fade so even the last item's
  // yoyo can revert fully — otherwise the last category visually "stays
  // highlighted" and reads as an extra underline at the end of the clip.
  // ------------------------------------------------------------------
  const outroFadeSec = 10 / 30;
  const spotlightStart = rowStart + parsed.length * rowStep + 0.2;
  const safetyMargin = 0.7;
  const spotlightEndCap = clipDurationSec - outroFadeSec - safetyMargin;
  const spotlightWindow = Math.max(0, spotlightEndCap - spotlightStart);
  const enableSpotlight = spotlightWindow > 0.5;
  const spotStep = enableSpotlight ? spotlightWindow / parsed.length : 0;

  if (enableSpotlight) {
    parsed.forEach((item, i) => {
      const at = spotlightStart + i * spotStep;
      const dur = Math.min(spotStep * 0.7, 0.3);
      // Icon pulse — scale only, no color (color persistence is risky).
      tweens.push(t.to(`#${iconIds[i]}`, {
        scale: 1.18,
        duration: dur * 0.5,
        ease: "raw:\"power2.out\"",
        yoyo: true,
        repeat: 1,
      }, at));
      // Title color flash — pop into brand color, then back to fg.
      tweens.push(t.to(`#${titleIds[i]}`, {
        color: item.color,
        duration: dur * 0.4,
        ease: "raw:\"power2.out\"",
        yoyo: true,
        repeat: 1,
      }, at));
      // Number color pulses too.
      tweens.push(t.to(`#${numIds[i]}`, {
        color: item.color,
        duration: dur * 0.4,
        ease: "raw:\"power2.out\"",
        yoyo: true,
        repeat: 1,
      }, at));
    });
  }

  // Safety net: hard-reset title + number colors to baseline a beat before
  // the outro fade kicks in, so no yoyo-in-flight can leave a row stuck
  // in brand colour.
  const resetAt = clipDurationSec - outroFadeSec - 0.15;
  titleIds.forEach((id) => {
    tweens.push(t.set(`#${id}`, { color: themeColors.fg }, resetAt));
  });
  numIds.forEach((id) => {
    tweens.push(t.set(`#${id}`, { color: withAlpha(themeColors.fg, 0.45) }, resetAt));
  });
  iconIds.forEach((id) => {
    tweens.push(t.set(`#${id}`, { scale: 1 }, resetAt));
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
